import React, { useMemo, useState } from "react";

const CashflowPage = () => {
  // =====================================================================
  // DUMMY CONTEXT – nanti diganti dari JWT / backend:
  // role: TRADER | INVESTOR | ANALYST (di sini kita pakai TRADER sebagai contoh)
  // =====================================================================
  const currentRole = "TRADER"; // dummy only

  // =====================================================================
  // 1) WALLET SUMMARY (user-level)
  //    Ini representasi ledger aggregated for user saat ini.
  // =====================================================================
  const walletSummary = {
    currency: "USD",
    available: 1245.75,
    pending: 380.0,
    locked: 120.0,
    lifetimeInflow: 5320.5, // semua fee yang pernah masuk
    lifetimeOutflow: 3954.75, // semua payout / biaya yang pernah dibayar
    nextPayoutDate: "2025-02-01",
  };

  // Breakdown sumber uang for 30 hari terakhir (dummy)
  const last30dBreakdown = {
    performanceFee: 860.0,
    volumeRebate: 420.0,
    signalSubscription: 235.0,
    affiliateRebate: 95.0,
    platformFeePaid: 210.0, // yang dibayar user ke platform (misal withdrawal fee)
  };

  // =====================================================================
  // 2) LEDGER ENTRIES – ini simulasi tabel ledger single-user
  // =====================================================================
  const ledgerEntries = [
    {
      id: "L-2025-0010",
      date: "2025-01-19 21:05",
      role: "TRADER",
      type: "PERFORMANCE_FEE_IN",
      direction: "IN",
      amount: 320.5,
      currency: "USD",
      description: "Performance fee from Investor A (Jan 2025)",
      source: "Account: XAU Hybrid Scalper",
      balanceAfter: 1245.75,
    },
    {
      id: "L-2025-0009",
      date: "2025-01-18 13:22",
      role: "TRADER",
      type: "VOLUME_REBATE_IN",
      direction: "IN",
      amount: 180.0,
      currency: "USD",
      description: "Monthly volume rebate (broker ICMarkets)",
      source: "IB/Rebate",
      balanceAfter: 925.25,
    },
    {
      id: "L-2025-0008",
      date: "2025-01-17 09:41",
      role: "TRADER",
      type: "SIGNAL_SUBSCRIPTION_SHARE",
      direction: "IN",
      amount: 75.0,
      currency: "USD",
      description: "Signal subscription revenue – 5 investors (week 3)",
      source: "Signal: XAU Hybrid Desk",
      balanceAfter: 745.25,
    },
    {
      id: "L-2025-0007",
      date: "2025-01-15 16:02",
      role: "TRADER",
      type: "PAYOUT_REQUEST",
      direction: "OUT",
      amount: 500.0,
      currency: "USD",
      description: "Payout to bank account (manual review)",
      source: "Withdrawal",
      balanceAfter: 670.25,
    },
    {
      id: "L-2025-0006",
      date: "2025-01-15 16:02",
      role: "TRADER",
      type: "PLATFORM_FEE",
      direction: "OUT",
      amount: 10.0,
      currency: "USD",
      description: "Platform withdrawal fee 2%",
      source: "Platform",
      balanceAfter: 1170.25,
    },
    {
      id: "L-2025-0005",
      date: "2025-01-10 11:10",
      role: "TRADER",
      type: "PERFORMANCE_FEE_IN",
      direction: "IN",
      amount: 210.0,
      currency: "USD",
      description: "Performance fee from Investor B (Dec 2024)",
      source: "Account: XAU Hybrid Scalper",
      balanceAfter: 1180.25,
    },
    {
      id: "L-2025-0004",
      date: "2025-01-05 08:00",
      role: "TRADER",
      type: "VOLUME_REBATE_IN",
      direction: "IN",
      amount: 240.0,
      currency: "USD",
      description: "Volume rebate (Dec 2024)",
      source: "IB/Rebate",
      balanceAfter: 970.25,
    },
    {
      id: "L-2025-0003",
      date: "2024-12-28 18:45",
      role: "TRADER",
      type: "SIGNAL_SUBSCRIPTION_SHARE",
      direction: "IN",
      amount: 160.0,
      currency: "USD",
      description: "Signal subscription revenue – 8 investors (Dec)",
      source: "Signal: XAU Hybrid Desk",
      balanceAfter: 730.25,
    },
    {
      id: "L-2024-0002",
      date: "2024-12-20 10:22",
      role: "TRADER",
      type: "PAYOUT_REQUEST",
      direction: "OUT",
      amount: 450.0,
      currency: "USD",
      description: "Payout to bank account",
      source: "Withdrawal",
      balanceAfter: 570.25,
    },
    {
      id: "L-2024-0001",
      date: "2024-12-01 09:00",
      role: "TRADER",
      type: "MANUAL_ADJUSTMENT_IN",
      direction: "IN",
      amount: 100.0,
      currency: "USD",
      description: "Manual adjustment (promo / bonus)",
      source: "Admin",
      balanceAfter: 1020.25,
    },
  ];

  // Filter / sort ledger (kalau nanti mau ada filter range tanggal dsb)
  const [filterType, setFilterType] = useState("ALL");
  const filteredLedger = useMemo(() => {
    if (filterType === "ALL") return ledgerEntries;
    return ledgerEntries.filter((e) => e.type === filterType);
  }, [ledgerEntries, filterType]);

  // =====================================================================
  // 3) PLATFORM OVERVIEW (ADMIN LENS – dummy saja)
  // =====================================================================
  const isAdminLensDummy = true; // nanti diganti role ADMIN dari backend

  const platformOverview = {
    totalInvestors: 1843,
    totalTraders: 126,
    totalAnalysts: 37,
    totalActiveSubscriptions: 932,
    monthlyGrossFees: 43210.5, // semua fee dibayar investor
    monthlyPayoutToTraders: 30120.0,
    monthlyPayoutToAnalysts: 5920.0,
    monthlyPlatformShare: 7160.5,
    gmvAum: 1280000, // perkiraan AUM di broker (dummy)
  };

  const positive = (v) => v >= 0;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>Cashflow & earnings</h2>
          <p className="muted">
            Summary arus kas for {currentRole.toLowerCase()}.
            Frontend ini masih dummy – di versi live, semua angka diambil dari
            ledger backend (no-custody model: dana tetap di broker, platform
            hanya mengelola fee, rebate, and payout).
          </p>
        </div>
      </div>

      {/* WALLET SUMMARY */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-title-row">
          <h3>Wallet summary</h3>
          <span className="badge-soft">On-platform balance (ledger)</span>
        </div>

        <div className="metrics-grid">
          <div className="metric-pill">
            <div className="metric-label">Available balance</div>
            <div className="metric-value">
              ${walletSummary.available.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="metric-pill">
            <div className="metric-label">Pending payout</div>
            <div className="metric-value">
              ${walletSummary.pending.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="metric-pill">
            <div className="metric-label">Locked / reserve</div>
            <div className="metric-value">
              ${walletSummary.locked.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="metric-pill">
            <div className="metric-label">Lifetime inflow</div>
            <div className="metric-value">
              +$
              {walletSummary.lifetimeInflow.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="metric-pill">
            <div className="metric-label">Lifetime outflow</div>
            <div className="metric-value">
              -$
              {walletSummary.lifetimeOutflow.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        </div>

        <p className="muted" style={{ marginTop: 8 }}>
          Next payout window: <strong>{walletSummary.nextPayoutDate}</strong>{" "}
          (dummy). Di backend, jadwal payout & status akan diambil dari tabel
          ledger + payout_requests.
        </p>
      </div>

      {/* LAST 30D BREAKDOWN */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-title-row">
          <h3>Last 30 days breakdown</h3>
          <span className="badge-soft">
            Fee &amp; rebate dari investor → trader / analyst → platform
          </span>
        </div>

        <div className="metrics-grid">
          <div className="metric-pill">
            <div className="metric-label">Performance fee</div>
            <div className="metric-value">
              +${last30dBreakdown.performanceFee.toFixed(2)}
            </div>
            <div className="metric-sub">
              Dari copy-trading / profit share investor.
            </div>
          </div>
          <div className="metric-pill">
            <div className="metric-label">Volume rebate</div>
            <div className="metric-value">
              +${last30dBreakdown.volumeRebate.toFixed(2)}
            </div>
            <div className="metric-sub">
              Rebate lot dari broker (IB / partner).
            </div>
          </div>
          <div className="metric-pill">
            <div className="metric-label">Signal subscription</div>
            <div className="metric-value">
              +${last30dBreakdown.signalSubscription.toFixed(2)}
            </div>
            <div className="metric-sub">
              Revenue dari follower signal (share analis & platform).
            </div>
          </div>
          <div className="metric-pill">
            <div className="metric-label">Affiliate rebate</div>
            <div className="metric-value">
              +${last30dBreakdown.affiliateRebate.toFixed(2)}
            </div>
            <div className="metric-sub">
              Commission referral user baru.
            </div>
          </div>
          <div className="metric-pill">
            <div className="metric-label">Platform fee paid</div>
            <div className="metric-value">
              -${last30dBreakdown.platformFeePaid.toFixed(2)}
            </div>
            <div className="metric-sub">
              Misal: withdrawal fee, subscription yg dibayar ke platform.
            </div>
          </div>
        </div>

        <p className="muted" style={{ marginTop: 8 }}>
          Di backend, semua angka ini cukup diambil dari{" "}
          <code>ledger_entries</code> dengan filter{" "}
          <code>created_at &gt;= NOW() - 30 days</code> and{" "}
          <code>entry_type</code> masing-masing.
        </p>
      </div>

      {/* LEDGER TABLE (USER VIEW) */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-title-row">
          <h3>Ledger entries (user wallet)</h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label className="filter-label" style={{ marginBottom: 0 }}>
              Type
              <select
                className="filter-input"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="ALL">All types</option>
                <option value="PERFORMANCE_FEE_IN">Performance fee</option>
                <option value="VOLUME_REBATE_IN">Volume rebate</option>
                <option value="SIGNAL_SUBSCRIPTION_SHARE">Signal revenue</option>
                <option value="AFFILIATE_REBATE_IN">Affiliate</option>
                <option value="PAYOUT_REQUEST">Payout</option>
                <option value="PLATFORM_FEE">Platform fee</option>
              </select>
            </label>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="metrics-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Role</th>
                <th>Type</th>
                <th>Direction</th>
                <th>Amount</th>
                <th>Currency</th>
                <th>Source</th>
                <th>Description</th>
                <th>Balance after</th>
              </tr>
            </thead>
            <tbody>
              {filteredLedger.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center" }}>
                    Tidak ada entri ledger (dummy).
                  </td>
                </tr>
              ) : (
                filteredLedger.map((e) => (
                  <tr key={e.id}>
                    <td>{e.id}</td>
                    <td>{e.date}</td>
                    <td>{e.role}</td>
                    <td>{e.type}</td>
                    <td>{e.direction}</td>
                    <td
                      className={
                        e.direction === "IN" ? "pnl-positive" : "pnl-negative"
                      }
                    >
                      {e.direction === "IN" ? "+" : "-"}$
                      {e.amount.toFixed(2)}
                    </td>
                    <td>{e.currency}</td>
                    <td>{e.source}</td>
                    <td>{e.description}</td>
                    <td>
                      $
                      {e.balanceAfter.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PLATFORM OVERVIEW (ADMIN LENS) */}
      {isAdminLensDummy && (
        <div className="card">
          <div className="card-title-row">
            <h3>Platform cashflow overview (admin lens, dummy)</h3>
            <span className="badge-soft">
              Hanya dummy for gambaran tim admin / finance
            </span>
          </div>

          <div className="metrics-grid">
            <div className="metric-pill">
              <div className="metric-label">Total investors</div>
              <div className="metric-value">
                {platformOverview.totalInvestors.toLocaleString()}
              </div>
            </div>
            <div className="metric-pill">
              <div className="metric-label">Total traders</div>
              <div className="metric-value">
                {platformOverview.totalTraders.toLocaleString()}
              </div>
            </div>
            <div className="metric-pill">
              <div className="metric-label">Total analysts</div>
              <div className="metric-value">
                {platformOverview.totalAnalysts.toLocaleString()}
              </div>
            </div>
            <div className="metric-pill">
              <div className="metric-label">Active subscriptions</div>
              <div className="metric-value">
                {platformOverview.totalActiveSubscriptions.toLocaleString()}
              </div>
            </div>
            <div className="metric-pill">
              <div className="metric-label">GMV / AUM (est.)</div>
              <div className="metric-value">
                $
                {platformOverview.gmvAum.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
              </div>
            </div>
          </div>

          <div className="metrics-grid" style={{ marginTop: 16 }}>
            <div className="metric-pill">
              <div className="metric-label">Gross fees from investors (30d)</div>
              <div className="metric-value">
                ${platformOverview.monthlyGrossFees.toFixed(2)}
              </div>
            </div>
            <div className="metric-pill">
              <div className="metric-label">Payout to traders (30d)</div>
              <div className="metric-value">
                ${platformOverview.monthlyPayoutToTraders.toFixed(2)}
              </div>
            </div>
            <div className="metric-pill">
              <div className="metric-label">Payout to analysts (30d)</div>
              <div className="metric-value">
                ${platformOverview.monthlyPayoutToAnalysts.toFixed(2)}
              </div>
            </div>
            <div className="metric-pill">
              <div className="metric-label">Platform share (30d)</div>
              <div className="metric-value">
                ${platformOverview.monthlyPlatformShare.toFixed(2)}
              </div>
            </div>
          </div>

          <p className="muted" style={{ marginTop: 8 }}>
            Di backend, ini cukup pakai{" "}
            <code>SUM(amount)</code> dari tabel ledger dengan{" "}
            <code>entry_type</code>:
            <br />
            <code>INVESTOR_FEE_DEBIT</code>,{" "}
            <code>TRADER_FEE_CREDIT</code>,{" "}
            <code>ANALYST_FEE_CREDIT</code>,{" "}
            <code>PLATFORM_MARGIN</code>, dsb – lalu digroup per 30 hari /
            per bulan.
          </p>
        </div>
      )}
    </div>
  );
};

export default CashflowPage;
